"use client";

import { useCallback, useMemo, useState } from "react";
import { Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import { useCallScoringDraftEmail } from "@/hooks/use-call-scoring-draft-email";
import { useCallScoringDraftEmailFeedback } from "@/hooks/use-call-scoring-draft-email-feedback";
import { DetailDrawer, DetailDrawerSkeleton } from "@/components/ui/detail-drawer";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import type { CallScoringDraftEmailResponse } from "@/api-client/call-scoring";
import { copyToClipboard } from "@/lib/clipboard";
import { captureGlobal } from "@/lib/analytics";

interface EmailDraftDrawerProps {
  meetingId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function hasDraftContent(
  draft: CallScoringDraftEmailResponse | undefined
): draft is Extract<CallScoringDraftEmailResponse, { subject: unknown; message: unknown }> {
  return Boolean(draft && "subject" in draft && "message" in draft);
}

export function EmailDraftDrawer({
  meetingId,
  open,
  onOpenChange,
}: EmailDraftDrawerProps) {
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
  const [subjectOverride, setSubjectOverride] = useState<{
    isLiked: boolean;
    isDisliked: boolean;
  } | null>(null);
  const [messageOverride, setMessageOverride] = useState<{
    isLiked: boolean;
    isDisliked: boolean;
  } | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "subject" | "message">("idle");

  const { data, isLoading, isError, isFetching } = useCallScoringDraftEmail({
    meetingId,
    enabled: open,
  });
  const noEmailFound = data?.status === "NO_EMAIL_FOUND";
  const draftHasContent = hasDraftContent(data);

  const feedbackMutation = useCallScoringDraftEmailFeedback({
    meetingId,
  });

  const draft = useMemo(
    () => ({
      subject: draftHasContent ? data.subject.description : "",
      message: draftHasContent ? data.message.description : "",
    }),
    [data, draftHasContent],
  );

  const copySubject = useCallback(async () => {
    const copied = await copyToClipboard(draft.subject);
    if (!copied) return;
    captureGlobal("email_copied_or_sent", { call_id: meetingId });
    setCopyState("subject");
    setTimeout(() => setCopyState("idle"), 1200);
  }, [draft.subject, meetingId]);

  const copyMessage = useCallback(async () => {
    const copied = await copyToClipboard(draft.message);
    if (!copied) return;
    captureGlobal("email_copied_or_sent", { call_id: meetingId });
    setCopyState("message");
    setTimeout(() => setCopyState("idle"), 1200);
  }, [draft.message, meetingId]);

  const activeIconColor = "var(--base-primary, #FF531B)";
  const inactiveIconColor = "var(--base-muted-foreground, #647A97)";

  const canSubmit = useMemo(() => {
    const isRefetching = Boolean(isFetching);
    return draftHasContent && !isRefetching;
  }, [draftHasContent, isFetching]);

  const subjectIsLiked =
    subjectOverride?.isLiked ?? (draftHasContent ? data.subject.is_liked : false);
  const subjectIsDisliked =
    subjectOverride?.isDisliked ?? (draftHasContent ? data.subject.is_disliked : false);
  const messageIsLiked =
    messageOverride?.isLiked ?? (draftHasContent ? data.message.is_liked : false);
  const messageIsDisliked =
    messageOverride?.isDisliked ?? (draftHasContent ? data.message.is_disliked : false);

  const submitDraftFeedback = useCallback(
    async (section: "subject" | "message", action: "like" | "dislike") => {
      if (!draftHasContent) return;

      const currentIsLiked = section === "subject" ? subjectIsLiked : messageIsLiked;
      const currentIsDisliked = section === "subject" ? subjectIsDisliked : messageIsDisliked;

      const nextPayload =
        action === "like"
          ? {
              section,
              is_liked: !currentIsLiked,
              is_disliked: false,
            }
          : {
              section,
              is_liked: false,
              is_disliked: !currentIsDisliked,
            };

      if (section === "subject") {
        setSubjectOverride({ isLiked: nextPayload.is_liked, isDisliked: nextPayload.is_disliked });
      } else {
        setMessageOverride({ isLiked: nextPayload.is_liked, isDisliked: nextPayload.is_disliked });
      }

      const actionId = `${section}_${action}`;
      setLoadingActionId(actionId);
      try {
        await feedbackMutation.mutateAsync(nextPayload);
      } finally {
        setLoadingActionId(null);
        if (section === "subject") setSubjectOverride(null);
        else setMessageOverride(null);
      }
    },
    [
      draftHasContent,
      feedbackMutation,
      subjectIsLiked,
      subjectIsDisliked,
      messageIsLiked,
      messageIsDisliked,
    ],
  );

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Email Generator"
      side="right"
      className="sm:max-w-2xl"
    >
      {isLoading ? <DetailDrawerSkeleton /> : null}

      {!isLoading && isError ? (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4">
          <Text as="p" size="sm" weight="semibold" color="default" className="text-destructive">
            Failed to generate email draft.
          </Text>
          <Text as="p" size="sm" weight="normal" color="muted" className="mt-1">
            Please try again in a moment.
          </Text>
        </div>
      ) : null}

      {!isLoading && !isError && noEmailFound ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <Text as="p" size="sm" weight="semibold" color="default">
            No email found for this call
          </Text>
          <Text as="p" size="sm" weight="normal" color="muted" className="mt-1">
            We couldn&apos;t generate a draft because no contact email is available.
          </Text>
        </div>
      ) : null}

      {!isLoading && !isError && !noEmailFound ? (
        <div className="space-y-4">
          <div>
            <Text as="p" size="sm" weight="semibold" color="default" className="mb-2">
              Subject
            </Text>
            <div className="rounded-lg border border-border bg-card px-4 py-3">
              <Text as="p" size="sm" weight="normal" color="default">
                {draft.subject}
              </Text>
            </div>
            <div className="mt-2 flex items-center text-muted-foreground">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 gap-2 py-2"
                onClick={copySubject}
                aria-label="Copy subject"
              >
                <Copy className="h-4 w-4" strokeWidth={2.25}  />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 gap-2 py-2"
                disabled={!canSubmit}
                loading={loadingActionId === "subject_like"}
                onClick={() => submitDraftFeedback("subject", "like")}
                aria-label="Like draft"
              >
                <ThumbsUp
                  className="h-4 w-4"
                  fill={subjectIsLiked ? activeIconColor : "none"}
                  stroke={subjectIsLiked ? activeIconColor : inactiveIconColor}
                  strokeWidth={2.25}
                />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 gap-2 py-2"
                disabled={!canSubmit}
                loading={loadingActionId === "subject_dislike"}
                onClick={() => submitDraftFeedback("subject", "dislike")}
                aria-label="Dislike draft"
              >
                <ThumbsDown
                  className="h-4 w-4"
                  fill={subjectIsDisliked ? activeIconColor : "none"}
                  stroke={subjectIsDisliked ? activeIconColor : inactiveIconColor}
                  strokeWidth={2.25}
                />
              </Button>
              {copyState === "subject" ? (
                <Text as="span" size="xs" weight="medium" color="muted">
                  Copied
                </Text>
              ) : null}
            </div>
          </div>

          <div>
            <Text as="p" size="sm" weight="semibold" color="default" className="mb-2">
              Message
            </Text>
            <div className="rounded-lg border border-border bg-card p-4">
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {draft.message}
              </pre>
            </div>
            <div className="mt-2 flex items-center text-muted-foreground">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 gap-2 py-2"
                onClick={copyMessage}
                aria-label="Copy message"
              >
                <Copy className="h-4 w-4" strokeWidth={2.25} />
              </Button>
            
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 gap-2 py-2"
                disabled={!canSubmit}
                loading={loadingActionId === "message_like"}
                onClick={() => submitDraftFeedback("message", "like")}
                aria-label="Like draft"
              >
                <ThumbsUp
                  className="h-4 w-4"
                  fill={messageIsLiked ? activeIconColor : "none"}
                  stroke={messageIsLiked ? activeIconColor : inactiveIconColor}
                  strokeWidth={2.25}
                />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 gap-2 py-2"
                disabled={!canSubmit}
                loading={loadingActionId === "message_dislike"}
                onClick={() => submitDraftFeedback("message", "dislike")}
                aria-label="Dislike draft"
              >
                <ThumbsDown
                  className="h-4 w-4"
                  fill={messageIsDisliked ? activeIconColor : "none"}
                  stroke={messageIsDisliked ? activeIconColor : inactiveIconColor}
                  strokeWidth={2.25}
                />
              </Button>
              {copyState === "message" ? (
                <Text as="span" size="xs" weight="medium" color="muted">
                  Copied
                </Text>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </DetailDrawer>
  );
}
